// Group page (SPEC §7.3): identity, current plan, plan history, 4 weeks of lessons, admin actions.

import type { ApiResult } from "../../api/client";
import { fetchGroup, fetchGroupLessons, setGroupArchived } from "../../api/groups";
import type { Group, GroupPeriod, Lesson } from "../../api/types";
import { addDays, mondayOf } from "../../dates";
import { formatDate, t } from "../../i18n/i18n";
import { navigate, renderCurrentRoute, setRedraw, type RouteMatch } from "../../router";
import { isAdmin, requireSchool } from "../../session";
import { buildDateNavigator } from "../../ui/dateNavigator";
import { createBadge, createButton, createElement } from "../../ui/dom";
import { describeProblem, showToast } from "../../ui/feedback";
import { createLoading, showPageProblem, startPage } from "../pageFrame";
import {
  classroomWithCenter, courseName, describeSlots, loadReferenceData, teacherName, type ReferenceData,
} from "../referenceData";
import { buildIdentityForm, buildPlanForm } from "./groupEditForms";
import { buildGroupLessonsTable } from "./groupLessonsTable";

const WEEKS_SHOWN: number = 4;

interface GroupPageState {
  group: Group;
  lessons: Lesson[];
  reference: ReferenceData;
  from: string;
  openForm: "identity" | "plan" | null;
}

export async function renderGroupPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const page: HTMLElement = startPage(container, t("groups.title"));
  page.append(createLoading());
  const from: string = mondayOf(route.query.get("from") ?? requireSchool().today);
  const state: GroupPageState | null = await loadState(route.params.id ?? "", from, page);
  if (state === null) {
    return;
  }

  const draw = (): void => {
    drawPage(container, state);
  };
  setRedraw(container, draw);
  draw();
}

async function loadState(groupId: string, from: string, page: HTMLElement): Promise<GroupPageState | null> {
  const group: ApiResult<Group> = await fetchGroup(groupId);
  const lessons: ApiResult<Lesson[]> = await fetchGroupLessons(groupId, from, addDays(from, WEEKS_SHOWN * 7 - 1));
  const reference: ApiResult<ReferenceData> = await loadReferenceData();
  for (const result of [group, lessons, reference]) {
    if (result.ok === false) {
      showPageProblem(page, result.problem);
      return null;
    }
  }

  if (group.ok === false || lessons.ok === false || reference.ok === false) {
    return null;
  }

  return { group: group.data, lessons: lessons.data, reference: reference.data, from, openForm: null };
}

function drawPage(container: HTMLElement, state: GroupPageState): void {
  const page: HTMLElement = startPage(container, state.group.name);
  page.append(buildDetails(state.group, state.reference));
  if (isAdmin()) {
    page.append(buildAdminActions(container, state));
    appendOpenForm(page, state);
  }

  page.append(createElement("h2", "", t("groups.periods")), buildPeriodsTable(state.group.periods, state.reference));
  page.append(createElement("h2", "", t("groups.lessons")), buildDateNavigator(state.from, WEEKS_SHOWN * 7,
    (date: string): void => {
      navigate(`/groups/${state.group.id}?from=${mondayOf(date)}`);
    }), buildGroupLessonsTable(state.lessons));
}

function buildDetails(group: Group, reference: ReferenceData): HTMLElement {
  const card: HTMLDivElement = createElement("div", "card");
  const lines: string[] = [
    `${t("groups.course")}: ${courseName(reference, group.courseId)}`,
    `${t("groups.startDate")}: ${formatDate(group.startDate)}`,
    `${t("groups.endDate")}: ${group.endDate === null ? t("app.none") : formatDate(group.endDate)}`,
    `${t("groups.currentPlan")}: ${describePeriod(group.currentPeriod, reference)}`,
  ];
  for (const line of lines) {
    card.append(createElement("p", "", line));
  }

  if (group.note !== null) {
    card.append(createElement("p", "muted", group.note));
  }

  if (group.archived && group.archivedOn !== null) {
    card.append(createBadge(t("groups.archivedOn", { date: group.archivedOn }), "neutral"));
  }

  return card;
}

function describePeriod(period: GroupPeriod, reference: ReferenceData): string {
  return [teacherName(reference, period.teacherId), classroomWithCenter(reference, period.classroomId),
    describeSlots(period.slots)].join(" · ");
}

function buildPeriodsTable(periods: GroupPeriod[], reference: ReferenceData): HTMLElement {
  const list: HTMLUListElement = createElement("ul");
  for (const period of periods) {
    list.append(createElement("li", "", `${t("groups.validFrom")} ${formatDate(period.validFrom)}: ${
      describePeriod(period, reference)}`));
  }

  return list;
}

function buildAdminActions(container: HTMLElement, state: GroupPageState): HTMLElement {
  const actions: HTMLDivElement = createElement("div", "lesson-actions");
  const toggle = (form: GroupPageState["openForm"]): void => {
    state.openForm = state.openForm === form ? null : form;
    drawPage(container, state);
  };
  actions.append(createButton(t("groups.editIdentity"), (): void => {
    toggle("identity");
  }), createButton(t("groups.changePlan"), (): void => {
    toggle("plan");
  }), buildArchiveButton(state.group));
  return actions;
}

function buildArchiveButton(group: Group): HTMLElement {
  const label: string = group.archived ? t("app.unarchive") : t("app.archive");
  return createButton(label, async (): Promise<void> => {
    const result: ApiResult<Group> = await setGroupArchived(group.id, group.archived === false, group.version);
    showToast(result.ok ? t("app.saved") : describeProblem(result.problem));
    await renderCurrentRoute();
  });
}

function appendOpenForm(page: HTMLElement, state: GroupPageState): void {
  const onSaved = (): void => {
    showToast(t("app.saved"));
    void renderCurrentRoute();
  };
  if (state.openForm === "identity") {
    page.append(buildIdentityForm(state.group, onSaved));
  }

  if (state.openForm === "plan") {
    page.append(buildPlanForm(state.group, state.reference, onSaved));
  }
}
